import { useEffect, useState } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '../../types/dashboardMetrics.types'
import {
  getPatientsAgeOverview,
  getPatientsAgePatients,
  type PatientsAgeAxisConfig,
  type PatientsAgeChartPoint,
} from '@services/api/dashboard/patientsAge.api'
import {
  getPatientsAgeAxisConfig,
  getPatientsAgeData,
} from '@components/pages/Dashboard/Operations/operationsMockData'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import {
  getPatientsAgeDetailMetrics,
  getPatientsAgeDetailTableData,
  type PatientsAgeDetailRow,
} from '@pages/Dashboard/PatientsAgeDetail/patientsAgeDetail.mockData'

export type PatientsAgeOverviewState = {
  metrics: DashboardMetricItemData[]
  chartData: PatientsAgeChartPoint[]
  axis: PatientsAgeAxisConfig
  loading: boolean
  error: string | null
  /** Overview response has arrived (`overview != null`). */
  hasPayload: boolean
}

/**
 * Loads the Operations → Patients age overview (KPI metrics + chart + axis).
 * Falls back to mock data while loading or on error.
 */
export function usePatientsAgeOverview(
  range: RangeKey,
  date?: string,
  enabled = true,
  queued = false,
): PatientsAgeOverviewState {
  const [overview, setOverview] = useState<{
    metrics: DashboardMetricItemData[]
    chartData: PatientsAgeChartPoint[]
    axis: PatientsAgeAxisConfig
  } | null>(null)
  const waiting = queued && !enabled
  const [loading, setLoading] = useState(enabled || waiting)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) {
      setLoading(waiting)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    getPatientsAgeOverview(range, date)
      .then((result) => {
        if (!cancelled) setOverview(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load patients age')
        setOverview(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [range, date, enabled, waiting])

  const useMocks = isDashboardDevMocksEnabled()
  const fallbackMetrics = getPatientsAgeDetailMetrics(range)
  const fallbackChartData = getPatientsAgeData(range)
  const fallbackAxis = getPatientsAgeAxisConfig(range)

  return {
    metrics: overview?.metrics ?? (useMocks ? fallbackMetrics : []),
    chartData: overview?.chartData ?? (useMocks ? fallbackChartData : []),
    axis: overview?.axis ?? fallbackAxis,
    loading: loading || waiting,
    error,
    hasPayload: overview != null,
  }
}

export type PatientsAgePatientsState = {
  rows: PatientsAgeDetailRow[]
  loading: boolean
  error: string | null
}

/** Loads the Patients age detail table rows with mock fallback. */
export function usePatientsAgePatients(
  range: RangeKey,
  date?: string,
): PatientsAgePatientsState {
  const [rows, setRows] = useState<PatientsAgeDetailRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getPatientsAgePatients(range, date)
      .then((result) => {
        if (!cancelled) setRows(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load patients age list',
        )
        setRows(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [range, date])

  return {
    rows: rows ?? getPatientsAgeDetailTableData(range),
    loading,
    error,
  }
}
